if (!customElements.get('rtd-sticky-purchase-bar')) {
  customElements.define(
    'rtd-sticky-purchase-bar',
    class RtdStickyPurchaseBar extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.titleElement = this.querySelector('[data-rtd-sticky-title]');
        this.imageWrapper = this.querySelector('[data-rtd-sticky-image-wrapper]');
        this.imageElement = this.querySelector('[data-rtd-sticky-image]');
        this.submitButton = this.querySelector('[data-rtd-sticky-submit]');
        this.submitLabel = this.querySelector('[data-rtd-sticky-submit-label]');
        this.submitSpinner = this.querySelector('.loading__spinner');
        this.spacer = this.parentElement?.querySelector('[data-rtd-sticky-spacer]');
        this.scope = this.closest('rtd-course-selector, rtd-in-person-course, rtd-learning-companion');
        this.mode = this.dataset.rtdStickyMode;
        this.source = null;
        this.sourceButton = null;
        this.frame = null;
        this.observer = null;
        this.resizeObserver = null;
        this.visualViewport = window.visualViewport || null;
        this.imageRequestId = 0;

        this.onSubmit = this.onSubmit.bind(this);
        this.onScroll = this.scheduleSync.bind(this);
        this.onResize = this.scheduleSync.bind(this);
        this.onOrientationChange = this.scheduleSync.bind(this);
        this.onVariantChange = this.scheduleSync.bind(this);
        this.onMutations = this.onMutations.bind(this);
        this.onImageLoad = this.onImageLoad.bind(this);
        this.onImageError = this.onImageError.bind(this);
        this.imageElement?.addEventListener('load', this.onImageLoad);
        this.imageElement?.addEventListener('error', this.onImageError);
        this.submitButton?.addEventListener('click', this.onSubmit);
        this.scope?.addEventListener('rtd:variant-change', this.onVariantChange);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize);
        window.addEventListener('orientationchange', this.onOrientationChange);
        document.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
        this.visualViewport?.addEventListener('scroll', this.onScroll, { passive: true });
        this.visualViewport?.addEventListener('resize', this.onResize);

        if (this.scope) {
          this.observer = new MutationObserver(this.onMutations);
          this.observer.observe(this.scope, {
            attributes: true,
            characterData: true,
            childList: true,
            subtree: true,
            attributeFilter: ['aria-disabled', 'aria-haspopup', 'class', 'disabled', 'hidden'],
          });
        }

        if (window.ResizeObserver) {
          this.resizeObserver = new ResizeObserver(() => this.updateSpacer());
          this.resizeObserver.observe(this);
        }

        this.sync();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('orientationchange', this.onOrientationChange);
        document.removeEventListener('scroll', this.onScroll, true);
        this.visualViewport?.removeEventListener('scroll', this.onScroll);
        this.visualViewport?.removeEventListener('resize', this.onResize);
        this.scope?.removeEventListener('rtd:variant-change', this.onVariantChange);
        this.imageElement?.removeEventListener('load', this.onImageLoad);
        this.imageElement?.removeEventListener('error', this.onImageError);
        this.submitButton?.removeEventListener('click', this.onSubmit);
        this.observer?.disconnect();
        this.resizeObserver?.disconnect();
        if (this.frame !== null) window.cancelAnimationFrame(this.frame);
        this.frame = null;
        this.clearSpacer();
        this.initialized = false;
      }

      scheduleSync() {
        if (this.frame !== null) return;
        this.frame = window.requestAnimationFrame(() => {
          this.frame = null;
          this.sync();
        });
      }

      onMutations(records) {
        const sourceChanged = records.some(
          (record) => !this.contains(record.target) && record.target !== this.spacer
        );
        if (sourceChanged) this.scheduleSync();
      }

      sync() {
        if (!this.scope || window.Shopify?.designMode) {
          this.source = null;
          this.sourceButton = null;
          this.clearImage();
          this.setVisible(false);
          return;
        }

        const source = this.findSource();
        const sourceButton = source?.querySelector('[type="submit"]');
        if (!source || !sourceButton || !sourceButton.isConnected || this.isHidden(sourceButton)) {
          this.source = null;
          this.sourceButton = null;
          this.clearImage();
          this.setVisible(false);
          return;
        }

        this.source = source;
        this.sourceButton = sourceButton;
        this.updateContent();
        this.setVisible(this.hasSourceScrolledPastViewport(sourceButton));
      }

      hasSourceScrolledPastViewport(sourceButton) {
        const sourceBounds = sourceButton.getBoundingClientRect();
        const viewportTop = this.visualViewport?.offsetTop || 0;
        return sourceBounds.bottom <= viewportTop;
      }

      findSource() {
        const sources = Array.from(this.scope.querySelectorAll('[data-rtd-sticky-source]'));
        return sources.find((item) => !this.isHidden(item));
      }

      isHidden(element) {
        let current = element;
        while (current && current !== this.scope.parentElement) {
          if (current.hidden) return true;
          current = current.parentElement;
        }
        return false;
      }

      updateContent() {
        if (!this.source || !this.sourceButton) return;

        this.updateImage();

        const title =
          this.mode === 'in-person'
            ? [
                this.dataset.rtdStickyProductTitle,
                this.source.dataset.location,
                this.source.dataset.session,
              ]
                .filter(Boolean)
                .join(' - ')
            : [this.source.dataset.rtdStickyProductTitle, this.source.dataset.rtdStickyFormat]
                .filter(Boolean)
                .join(' - ');
        const label = this.sourceButton.querySelector('span:not(.loading__spinner)')?.textContent.trim() || '';
        const loading = this.sourceButton.classList.contains('loading');
        const disabled = this.sourceButton.disabled || this.sourceButton.getAttribute('aria-disabled') === 'true' || loading;

        const nextTitle = title || '';
        if (this.titleElement.textContent !== nextTitle) this.titleElement.textContent = nextTitle;
        if (this.submitLabel.textContent !== label) this.submitLabel.textContent = label;
        if (this.submitButton.disabled !== disabled) this.submitButton.disabled = disabled;
        this.submitButton.classList.toggle('loading', loading);
        this.submitSpinner?.classList.toggle('hidden', !loading);
        const accessibleLabel = label || 'Add to cart';
        if (this.submitButton.getAttribute('aria-label') !== accessibleLabel) {
          this.submitButton.setAttribute('aria-label', accessibleLabel);
        }
        if (this.submitButton.getAttribute('aria-busy') !== String(loading)) {
          this.submitButton.setAttribute('aria-busy', String(loading));
        }

        const hasPopup = this.sourceButton.getAttribute('aria-haspopup');
        if (hasPopup) {
          if (this.submitButton.getAttribute('aria-haspopup') !== hasPopup) {
            this.submitButton.setAttribute('aria-haspopup', hasPopup);
          }
        } else if (this.submitButton.hasAttribute('aria-haspopup')) {
          this.submitButton.removeAttribute('aria-haspopup');
        }
      }

      updateImage() {
        if (!this.imageWrapper || !this.imageElement || !this.source) return;

        const imageUrl = this.source.dataset.rtdStickyImageUrl || '';
        const imageAlt = this.source.dataset.rtdStickyImageAlt || this.source.dataset.rtdStickyProductTitle || '';

        if (!imageUrl) {
          this.clearImage();
          return;
        }

        const currentImageUrl = this.imageElement.dataset.rtdStickyImageUrl || '';
        if (currentImageUrl !== imageUrl) {
          this.requestImage(imageUrl, imageAlt);
          return;
        }
        this.imageElement.alt = imageAlt;

        if (this.imageElement.complete && this.imageElement.naturalWidth > 0) {
          this.showImage(imageUrl);
        }
      }

      requestImage(imageUrl, imageAlt) {
        const previousImage = this.imageElement;
        const image = previousImage.cloneNode(false);
        const requestId = String(++this.imageRequestId);

        previousImage.removeEventListener('load', this.onImageLoad);
        previousImage.removeEventListener('error', this.onImageError);
        image.removeAttribute('src');
        image.hidden = true;
        image.alt = imageAlt;
        image.dataset.rtdStickyImageUrl = imageUrl;
        image.dataset.rtdStickyImageRequest = requestId;
        previousImage.replaceWith(image);

        this.imageElement = image;
        image.addEventListener('load', this.onImageLoad);
        image.addEventListener('error', this.onImageError);
        this.imageWrapper.hidden = true;
        image.setAttribute('src', imageUrl);
      }

      onImageLoad(event) {
        const imageElement = event?.currentTarget || this.imageElement;
        const imageUrl = imageElement?.dataset.rtdStickyImageUrl || '';
        const requestId = imageElement?.dataset.rtdStickyImageRequest || '';
        if (
          imageElement !== this.imageElement ||
          requestId !== String(this.imageRequestId) ||
          !imageUrl ||
          imageUrl !== this.source?.dataset.rtdStickyImageUrl
        ) {
          return;
        }
        this.showImage(imageUrl);
      }

      onImageError(event) {
        const imageElement = event?.currentTarget || this.imageElement;
        const imageUrl = imageElement?.dataset.rtdStickyImageUrl || '';
        const requestId = imageElement?.dataset.rtdStickyImageRequest || '';
        if (
          imageElement !== this.imageElement ||
          requestId !== String(this.imageRequestId) ||
          !imageUrl ||
          imageUrl !== this.source?.dataset.rtdStickyImageUrl
        ) {
          return;
        }
        this.clearImage();
      }

      showImage(imageUrl) {
        if (!this.imageElement || !this.imageWrapper || imageUrl !== this.source?.dataset.rtdStickyImageUrl) return;

        this.imageElement.hidden = false;
        this.imageWrapper.hidden = false;
      }

      clearImage() {
        if (!this.imageWrapper || !this.imageElement) return;

        this.imageElement.hidden = true;
        this.imageWrapper.hidden = true;
        this.imageRequestId += 1;
        this.imageElement.removeAttribute('src');
        this.imageElement.removeAttribute('alt');
        delete this.imageElement.dataset.rtdStickyImageUrl;
        delete this.imageElement.dataset.rtdStickyImageRequest;
      }

      setVisible(visible) {
        const shouldHide = !visible;
        if (this.hidden === shouldHide) return;

        this.hidden = shouldHide;
        if (visible) {
          this.updateSpacer();
        } else {
          this.clearSpacer();
        }
      }

      updateSpacer() {
        if (this.hidden || !this.spacer) return;
        if (this.spacer.hidden) this.spacer.hidden = false;
        const height = `${this.offsetHeight}px`;
        if (this.spacer.style.height !== height) this.spacer.style.height = height;
      }

      clearSpacer() {
        if (!this.spacer) return;
        if (!this.spacer.hidden) this.spacer.hidden = true;
        if (this.spacer.style.height) this.spacer.style.removeProperty('height');
      }

      onSubmit(event) {
        event.preventDefault();
        const source = this.findSource();
        const sourceButton = source?.querySelector('[type="submit"]');
        if (
          !source ||
          source !== this.source ||
          sourceButton !== this.sourceButton ||
          !sourceButton ||
          sourceButton.disabled ||
          sourceButton.getAttribute('aria-disabled') === 'true'
        ) {
          this.sync();
          return;
        }

        const form = sourceButton.form || sourceButton.closest('form');
        if (!form) {
          this.sync();
          return;
        }

        const productForm = sourceButton.closest('product-form');
        if (productForm) productForm.rtdReturnFocusTarget = this.submitButton;

        // Trigger the real control so mobile browsers do not depend on requestSubmit support.
        sourceButton.click();
      }
    }
  );
}
